import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, setAccessTokenSource } from './api';
import type { HostedSession } from './HostedAuth';

export function DemoAuth({ onSession }: { onSession: (session: HostedSession) => void }) {
  const [status, setStatus] = useState<HostedSession['status']>('loading');
  const [username, setUsername] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setAccessTokenSource(null);
    api
      .demoSession()
      .then((session) => {
        if (cancelled) return;
        setUsername(session.username);
        setStatus(session.signedIn ? 'signed-in' : 'signed-out');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
          setMessage('Sign-in is unavailable. Try refreshing the page.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const authenticate = useCallback(async (name: string, password: string) => {
    const session = await api.demoLogin(name, password);
    setUsername(session.username);
    setMessage(null);
    setStatus('signed-in');
  }, []);
  const signOut = useCallback(() => {
    void api
      .demoLogout()
      .then(() => {
        setUsername(null);
        setStatus('signed-out');
      })
      .catch(() => {
        setStatus('error');
        setMessage('Sign-out did not complete. Please try again.');
      });
  }, []);
  const signIn = useCallback(() => document.getElementById('demo-username')?.focus(), []);
  useEffect(() => {
    onSession({
      status,
      loginName: username,
      subject: null,
      message,
      signIn,
      signOut,
      authenticate,
    });
  }, [status, username, message, signIn, signOut, authenticate, onSession]);
  return null;
}

export function DemoLoginForm({ session }: { session: HostedSession }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await session.authenticate?.(username, password);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Sign-in did not complete.');
    } finally {
      setPassword('');
      setBusy(false);
    }
  }
  return (
    <form className="demo-login" onSubmit={(event) => void submit(event)}>
      <label htmlFor="demo-username">Username</label>
      <input
        id="demo-username"
        name="username"
        autoComplete="username"
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        required
        maxLength={40}
        disabled={busy}
      />
      <label htmlFor="demo-password">Password</label>
      <input
        id="demo-password"
        name="password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        required
        maxLength={256}
        disabled={busy}
      />
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      <button type="submit" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}
